import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeMode, type ThemePreference } from '../theme-mode';

const CHOICES: { value: ThemePreference; Icon: typeof LightModeIcon }[] = [
  { value: 'light', Icon: LightModeIcon },
  { value: 'dark', Icon: DarkModeIcon },
  { value: 'system', Icon: SettingsBrightnessIcon },
];

/**
 * Light, dark, or whatever the machine says.
 *
 * The button shows the theme **in use**, not the choice behind it: following
 * the machine at night and choosing dark look the same on screen, and the
 * icon describes the screen. The menu is where the choice is visible.
 */
export function ThemeSwitcher() {
  const { t } = useTranslation();
  const { preference, mode, setPreference } = useThemeMode();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const CurrentIcon = mode === 'dark' ? DarkModeIcon : LightModeIcon;

  return (
    <>
      <Tooltip title={t('theme.label')}>
        <IconButton
          color="inherit"
          onClick={(event: MouseEvent<HTMLElement>) => setAnchor(event.currentTarget)}
          aria-label={t('theme.label')}
          aria-haspopup="menu"
        >
          <CurrentIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {CHOICES.map(({ value, Icon }) => (
          <MenuItem
            key={value}
            selected={value === preference}
            onClick={() => {
              setPreference(value);
              setAnchor(null);
            }}
          >
            <ListItemIcon>
              <Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t(`theme.${value}`)} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
